import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fys = ["FY 2025-26", "FY 2024-25", "FY 2023-24"];
const depts = ["GST", "Income Tax", "ROC", "Accounting", "Audit", "TDS", "Litigation"];

export default function Documents() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", financial_year: "FY 2025-26", department: "GST", client_id: "", notes: "" });

  async function load() { const { data } = await api.get("/documents"); setRows(data); }
  useEffect(() => { load(); api.get("/clients").then(({ data }) => setClients(data)); }, []);

  async function submit(e) {
    e.preventDefault();
    const payload = { ...form };
    if (!payload.client_id) delete payload.client_id;
    await api.post("/documents", payload);
    toast.success("Document added");
    setOpen(false);
    setForm({ name: "", financial_year: "FY 2025-26", department: "GST", client_id: "", notes: "" });
    load();
  }

  async function del(id) { await api.delete(`/documents/${id}`); load(); }

  const clientMap = Object.fromEntries(clients.map(c => [c.id, c.client_name]));

  return (
    <div className="space-y-5" data-testid="documents-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Document Vault</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Documents</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-doc-btn" className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1.5" />Add Document</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Document (metadata)</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Name *</Label><Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>FY</Label>
                  <Select value={form.financial_year} onValueChange={v => setForm({ ...form, financial_year: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{fys.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Department</Label>
                  <Select value={form.department} onValueChange={v => setForm({ ...form, department: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{depts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Client</Label>
                <Select value={form.client_id} onValueChange={v => setForm({ ...form, client_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
              <DialogFooter><Button type="submit" className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-0 divide-y divide-border">
        {rows.length === 0 && (
          <div className="p-10 text-center">
            <FileText className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <div className="text-sm text-muted-foreground">No documents yet.</div>
          </div>
        )}
        {rows.map(d => (
          <div key={d.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors">
            <FileText className="w-4 h-4 text-blue-600" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{d.name}</div>
              <div className="text-[10px] text-muted-foreground">
                {d.financial_year} · {d.department}{d.client_id ? ` · ${clientMap[d.client_id]}` : ""} · by {d.uploaded_by_name}
              </div>
            </div>
            <Badge variant="outline" className="text-[10px]">{d.financial_year}</Badge>
            <Button variant="ghost" size="icon" onClick={() => del(d.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
      </Card>
    </div>
  );
}
